// editor-shortcuts — Ctrl-S / Cmd-S saves the code instead of opening the browser's
// "Save Page" dialog (Trello XmtbPDkG).
//
// Wired ONCE from main.js, uniformly across every surface that loads the editor bundle
// — like stage-prerun.js, and NOT dispatched by data-surface. Unlike stage-prerun.js it
// wires EAGERLY: above main.js's crossOriginIsolated early-return and outside
// loadProject().then(...), because the browser dialog is otherwise still live on the
// isolation-fallback page, on the "Could not load the editor." page, and for the seconds
// between page load and the payload resolving.
//
// Save itself is surface-dependent and there is no single save function, so this module
// owns a tiny REGISTRY instead: each saving surface's *-chrome.js registers a handler
// that both performs its own save AND writes its own status text (guided-make has no
// status element id to look up, only a closure-held element). A surface that registers
// nothing — read-only, review, challengeFeedback, debug, a non-owner on the editor
// surface — swallows the keystroke and does nothing else.
//
//   isSaveShortcut(e)          pure, DOM-free key predicate (also used by the unit spec)
//   registerSaveHandler(fn)    called from a *-chrome.js wire()
//   wireSaveShortcut(ctx)      ctx = { doc, frame } — install the listeners
//
// MUST NEVER THROW: wireSaveShortcut() runs inside main.js's IIFE, and a throw there
// kills every surface before editor-core is even constructed.

// How long a consumed keydown's "swallow the matching keyup" flag may live. macOS does
// not deliver keyup for ordinary keys while Meta is held, so the flag needs a floor of
// belt-and-braces clears (see attachFrame) — a stuck flag would silently eat a later
// genuine `s` keyup and hand the running program an unpaired on_key_down.
var KEYUP_SWALLOW_MS = 1500;

var saveHandlers = [];

// Pure + DOM-free so test/editor/editor-shortcuts.test.js can import it under Node.
// Deliberately does NOT consult e.repeat: every matching event must be preventDefault()-ed
// or a held Ctrl-S lets the browser dialog through, which is the one thing the anchor card
// forbids. Repeat/re-entrancy is handled by each registered handler's in-flight guard.
export function isSaveShortcut(e) {
  if (!e) { return false; }
  if (!(e.ctrlKey || e.metaKey)) { return false; }
  return typeof e.key === 'string' && e.key.toLowerCase() === 's';
}

// A `keyup` for the `s` key itself — the event the in-frame handler swallows after it has
// consumed the matching keydown. Bare modifier keyups are NOT this and flow untouched.
export function isSaveKeyup(e) {
  return !!e && typeof e.key === 'string' && e.key.toLowerCase() === 's';
}

export function registerSaveHandler(fn) {
  if (typeof fn === 'function' && saveHandlers.indexOf(fn) === -1) { saveHandlers.push(fn); }
}

// Test-only reset so the registry does not leak between specs.
export function __resetSaveHandlers() {
  saveHandlers = [];
}

// Ctrl-S is INERT (still swallowed, but performs no save) whenever its status text would
// be invisible: any blocking overlay, or fullscreen — both fullscreen targets leave the
// toolbar status elements outside the top layer.
//
// A single `.modal-backdrop` sweep is NOT sufficient and is a known trap:
//   1. .modal-backdrop         settings / share / assets / challenge-record / result modal,
//                              EXCEPT the challenge recording bar, which carries the class
//                              but is transparent + pointer-events:none during a session.
//   2. .editor-file-prompt     the file-name prompt editor-core builds — not a backdrop.
//   3. #project-rename-input   rendered UNCONDITIONALLY for owners and toggled via [hidden];
//                              a bare presence test would make Ctrl-S permanently inert on
//                              the primary in-app editor surface.
//   4. pc-feedback             the globally-mounted island. Its own open state is private,
//                              but pc-dialog's `open` reflects, so pierce the shadow root.
export function isSaveInert(doc) {
  try {
    if (!doc) { return true; }
    if (doc.fullscreenElement) { return true; }
    var backdrops = doc.querySelectorAll('.modal-backdrop:not([hidden])');
    for (var i = 0; i < backdrops.length; i++) {
      if (!backdrops[i].classList || !backdrops[i].classList.contains('rk-recording')) { return true; }
    }
    if (doc.querySelector('.editor-file-prompt:not([hidden])')) { return true; }
    if (doc.querySelector('#project-rename-input:not([hidden])')) { return true; }
    var feedback = doc.querySelector('pc-feedback');
    if (feedback && feedback.shadowRoot && feedback.shadowRoot.querySelector('pc-dialog[open]')) { return true; }
  } catch (e) { /* a probe failure must never block a save */ }
  return false;
}

function runSave(doc) {
  if (isSaveInert(doc)) { return; }
  for (var i = 0; i < saveHandlers.length; i++) {
    try { saveHandlers[i](); } catch (err) { console.error(err); }
  }
}

// The runner iframe is a separate (same-origin) document, so host-page keydown listeners
// never see keys pressed there — and stage-prerun.js focuses the frame after every run
// started from the stage control, making "focus is in the frame" the common post-run state.
//
// CAPTURE phase + stopImmediatePropagation is the only phase that beats runner-core's own
// canvas keydown/keyup handlers and challenge-replay's answer-key recorder, both of which
// are canvas listeners. Consequence, accepted: the running program never receives Ctrl-S,
// and a save during a recording never lands in the recorded inputs. Plain `s` with no
// modifier is completely unaffected.
//
// NOTE the attach signal: the runner document is NOT replaced per run (a run is pure
// postMessage), so this hooks the IFRAME ELEMENT's `load` event — which covers the initial
// navigation AND restart() / "Reload runner".
function attachFrame(frame, hostDoc) {
  var fdoc = null;
  try { fdoc = frame.contentDocument; } catch (e) { return; }
  if (!fdoc || !fdoc.addEventListener) { return; }
  // A re-navigation yields a NEW document, so re-attaching is correct; guard only against
  // attaching twice to the same one.
  if (fdoc.__pcSaveShortcutBound) { return; }
  try { fdoc.__pcSaveShortcutBound = true; } catch (e) { /* frozen doc — attach anyway */ }

  var swallowKeyup = false;
  var swallowTimer = null;
  function clearFlag() {
    swallowKeyup = false;
    if (swallowTimer) { clearTimeout(swallowTimer); swallowTimer = null; }
  }

  fdoc.addEventListener('keydown', function (e) {
    if (isSaveShortcut(e)) {
      if (e.preventDefault) { e.preventDefault(); }
      if (e.stopImmediatePropagation) { e.stopImmediatePropagation(); }
      swallowKeyup = true;
      if (swallowTimer) { clearTimeout(swallowTimer); }
      swallowTimer = setTimeout(clearFlag, KEYUP_SWALLOW_MS);
      runSave(hostDoc);
      return;
    }
    // Any subsequent keydown we did NOT consume clears the flag.
    clearFlag();
  }, true);

  fdoc.addEventListener('keyup', function (e) {
    var key = (e && typeof e.key === 'string') ? e.key.toLowerCase() : '';
    // Bare modifiers flow exactly as today — the user genuinely did press Ctrl/Cmd, and
    // suppressing them would change behaviour for a game that uses them. They also clear
    // the flag: on macOS this is the ONLY keyup Cmd-S produces.
    if (key === 'meta' || key === 'control') { clearFlag(); return; }
    if (swallowKeyup && isSaveKeyup(e)) {
      if (e.preventDefault) { e.preventDefault(); }
      if (e.stopImmediatePropagation) { e.stopImmediatePropagation(); }
      clearFlag();
    }
  }, true);

  fdoc.addEventListener('visibilitychange', clearFlag);
  try {
    if (fdoc.defaultView && fdoc.defaultView.addEventListener) {
      fdoc.defaultView.addEventListener('blur', clearFlag);
    }
  } catch (e) { /* cross-document access can throw in exotic states */ }
}

export function wireSaveShortcut(ctx) {
  try {
    ctx = ctx || {};
    var doc = ctx.doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.addEventListener) { return; }

    // Capture phase for symmetry with the in-frame listener, and so the shortcut beats any
    // future host handler. preventDefault() is UNCONDITIONAL on a match — the browser Save
    // Page dialog must never appear, whether or not a save follows.
    doc.addEventListener('keydown', function (e) {
      if (!isSaveShortcut(e)) { return; }
      if (e.preventDefault) { e.preventDefault(); }
      runSave(doc);
    }, true);

    var frame = ctx.frame || doc.getElementById('runner-frame');
    if (frame && frame.addEventListener) {
      frame.addEventListener('load', function () { attachFrame(frame, doc); });
      // The frame may already have navigated by the time this runs on a warm cache.
      attachFrame(frame, doc);
    }
  } catch (e) {
    console.error(e);
  }
}
